/**
 * An insight is one sentence, its evidence, and where it leads (ADR-033, kit §23).
 *
 * Every Progress chapter and focused page renders a list of these; the domain builds them so the
 * words are tested with the numbers. `text` is the sentence a person reads; `basedOn` always
 * starts with what it rests on ("18 logged days · 28 days"); `wait` marks a claim the data does
 * not yet earn — then `text` says what unlocks it and the row renders muted with a dashed tag.
 */
export interface InsightLink {
  to: string;
  label: string;
}

export type InsightTag = "est" | "cycle" | "phone" | "console" | "measured";

export interface Insight {
  /** Stable id, so a page can pick and a test can find one ("body.waist"). */
  key: string;
  text: string;
  basedOn: string;
  tag?: InsightTag;
  /** "after 9 more days" — present only when the claim is not yet earned. */
  wait?: string;
  link?: InsightLink;
}

/** The three fixed windows (principle 6): 7 days for counts, 28 by default, 90 for the season; 0 = all. */
export type Window = 7 | 28 | 90 | 0;

export const WINDOW_LABEL: Record<Window, string> = {
  7: "7 days",
  28: "28 days",
  90: "90 days",
  0: "All",
};

/** Number of days in a window as a sentence fragment. */
export function windowWord(w: Window): string {
  return w === 0 ? "since you started" : `${w} days`;
}

const SINCE_START = windowWord(0);

/**
 * "18 logged days · 28 days" — the standard evidence line. The All window is a phrase, not a
 * count, so it joins the part before it ("12 weigh-ins since you started"), and alone it names
 * the days: the row reads "based on …", and "based on since you started" is not a sentence.
 */
export function basedOn(...parts: (string | null | undefined)[]): string {
  const out: string[] = [];
  for (const p of parts) {
    if (!p) continue;
    if (p !== SINCE_START) out.push(p);
    else if (out.length) out[out.length - 1] += ` ${SINCE_START}`;
    else out.push(`the days ${SINCE_START}`);
  }
  return out.join(" · ");
}
